import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import {
  LoginRequest,
  LoginResponse,
  RefreshTokenResponse,
  CurrentUserResponse,
  UserInfo,
  TOKEN_KEYS,
  ERROR_CODES,
  ApiResponse
} from '@/types/auth.types';
import toast from 'react-hot-toast';
import { LTI_SIGNED_OUT } from '@/constants/constant-routes';

/** Set while the current session came from an LMS (LTI) launch. */
const LTI_SESSION_KEY = 'skillzen_lti_session';

class AuthService {
  private tokenRefreshPromise: Promise<any> | null = null;

  // Login user with credentials
  async login(credentials: LoginRequest): Promise<{ user: UserInfo }> {
    try {
      const response = await apiClient.post<LoginResponse>(API_ENDPOINTS.AUTH.LOGIN, credentials);
      
      if (response.data.success && response.data.data) {
        const { user_info, csrf_token } = response.data.data;

        // Store user info (tokens are in HttpOnly cookies, managed by server)
        this.setUser(user_info);

        // Store CSRF token in sessionStorage (cookie not readable cross-subdomain)
        if (csrf_token) {
          this.setCsrfToken(csrf_token);
        }

        return { user: user_info };
      }
      
      throw new Error(response.data.description || 'Login failed');
    } catch (error: any) {
      console.error('Login error:', error);
      this.clearSession();
     //Error mesage
      const errorCode = error.response?.data?.error?.error;
      const backendMessage = error.response?.data?.error?.error_description;
      const errorMessage = backendMessage ||
                          ERROR_CODES[errorCode as keyof typeof ERROR_CODES] ||
                          error.response?.data?.description ||
                          'Invalid email or password';

      throw new Error(errorMessage);
    }
  }

  // Logout user and clear session
  async logout(): Promise<void> {
    try {
      // Server reads access_token from HttpOnly cookie to identify the session
      await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT);
    } catch (error) {
      console.error('Logout API call failed, clearing client-side session anyway.', error);
    } finally {
      this.clearSession();
    }
  }

  // Refresh access token using refresh token (sent via HttpOnly cookie)
  async refreshAccessToken(): Promise<boolean> {
    // Prevent multiple simultaneous refresh requests
    if (this.tokenRefreshPromise) {
      return this.tokenRefreshPromise;
    }

    this.tokenRefreshPromise = this.performTokenRefresh();

    try {
      return await this.tokenRefreshPromise;
    } finally {
      this.tokenRefreshPromise = null;
    }
  }

  private async performTokenRefresh(): Promise<boolean> {
    try {
      // Refresh token is sent automatically via HttpOnly cookie (path-scoped)
      const response = await apiClient.post<RefreshTokenResponse>(API_ENDPOINTS.AUTH.REFRESH);

      if (response.data.success && response.data.data) {
        // Store new CSRF token from response
        const { csrf_token } = response.data.data;
        if (csrf_token) {
          this.setCsrfToken(csrf_token);
        }
        return true;
      }

      throw new Error('Token refresh failed');
    } catch (error) {
      console.error('Token refresh error:', error);
      this.clearSession();
      return false;
    }
  }

  // Validate current token (server reads from HttpOnly cookie)
  async validateToken(): Promise<boolean> {
    try {
      const response = await apiClient.get<ApiResponse<{
        valid: boolean;
        user_id: string;
        token_type: string;
        expires_in: number;
        message: string;
      }>>(API_ENDPOINTS.AUTH.VALIDATE);

      return response.data.success && response.data.data?.valid === true;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }

  // Get current user information
  async getCurrentUser(): Promise<CurrentUserResponse['data'] | null> {
    try {
      const response = await apiClient.get<CurrentUserResponse>(API_ENDPOINTS.AUTH.ME);
      
      if (response.data.success && response.data.data) {
        // Update stored user info and CSRF token
        this.setUser(response.data.data.user);
        if (response.data.data.auth_source === 'lti') {
          sessionStorage.setItem(LTI_SESSION_KEY, '1');
        } else {
          sessionStorage.removeItem(LTI_SESSION_KEY);
        }
        if (response.data.data.csrf_token) {
          this.setCsrfToken(response.data.data.csrf_token);
        }
        return response.data.data;
      }
      
      return null;
    } catch (error) {
      console.error('Get current user error:', error);
      
      // If unauthorized, try to refresh token
      if ((error as any).response?.status === 401) {
        const newToken = await this.refreshAccessToken();
        if (newToken) {
          // Retry the request
          return this.getCurrentUser();
        }
      }
      
      return null;
    }
  }

  setCsrfToken(token: string): void {
    sessionStorage.setItem('skillzen_csrf_token', token);
  }

  getCsrfToken(): string | null {
    return sessionStorage.getItem('skillzen_csrf_token');
  }

  setUser(user: UserInfo): void {
    localStorage.setItem(TOKEN_KEYS.USER_DATA, JSON.stringify(user));
  }

  getUser(): UserInfo | null {
    const userData = localStorage.getItem(TOKEN_KEYS.USER_DATA);
    return userData ? JSON.parse(userData) : null;
  }

  isAuthenticated(): boolean {
    // With HttpOnly cookies, we can't read tokens directly.
    // Check for session indicators: stored user data or CSRF cookie.
    const hasUser = !!this.getUser();
    const hasCsrf = !!this.getCsrfToken();

    if (!hasUser && !hasCsrf) return false;

    // If token is near-expiry, the interceptor will handle refresh automatically
    return true;
  }

  /**
   * Where to send the browser when the session ends. An LMS-launched session,
   * or any page inside a frame, gets the LTI signed-out page: the login page is
   * never allowed inside a frame. Call this BEFORE clearSession().
   */
  getSignedOutPath(): string {
    const inFrame = (() => {
      try { return window.self !== window.top; } catch { return true; }
    })();
    return inFrame || sessionStorage.getItem(LTI_SESSION_KEY) === '1' ? LTI_SIGNED_OUT : '/login';
  }

  clearSession(): void {
    // Check if there was actually a session before calling clear-cookies
    // (avoids clearing valid HttpOnly cookies during SSO callback initialization)
    const hadSession = !!this.getUser() || !!this.getCsrfToken();

    // Remove all auth-related items from localStorage (includes legacy token keys)
    Object.values(TOKEN_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });

    // Remove permissions and CSRF token from sessionStorage
    sessionStorage.removeItem('permissions');
    sessionStorage.removeItem('skillzen_csrf_token');
    sessionStorage.removeItem(LTI_SESSION_KEY);

    // Clear readable cookies (JS-accessible ones like CSRF)
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = name.trim() + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
    }

    // Only clear HttpOnly cookies via server if there was a prior session
    // (prevents destroying valid cookies set by SSO redirect before /me is called)
    if (hadSession) {
      apiClient.post(API_ENDPOINTS.AUTH.CLEAR_COOKIES).catch(() => {});
    }
  }

  // Helper methods
  hasRole(role: string): boolean {
    const user = this.getUser();
    if (!user) return false;
    
    return user.groups.some(group => 
      Array.isArray(group.roles) 
        ? group.roles.some(r => typeof r === 'string' ? r === role : r.role_key === role)
        : false
    );
  }

  hasAnyRole(roles: string[]): boolean {
    return roles.some(role => this.hasRole(role));
  }

  hasAllRoles(roles: string[]): boolean {
    return roles.every(role => this.hasRole(role));
  }

  getUserGroups(): string[] {
    const user = this.getUser();
    if (!user) return [];
    
    return user.groups.map(g => g.group_key);
  }

  getUserRoles(): string[] {
    const user = this.getUser();
    if (!user) return [];
    
    const roles = new Set<string>();
    user.groups.forEach(group => {
      if (Array.isArray(group.roles)) {
        group.roles.forEach(role => {
          const roleKey = typeof role === 'string' ? role : role.role_key;
          roles.add(roleKey);
        });
      }
    });
    
    return Array.from(roles);
  }
}

export const authService = new AuthService();
export default authService;